import React, { RefObject } from "react";
import Row, { RowData } from "./Body/Row";
import Footer from "./Footer/Footer";
import ScrollableColumns from "./Header/ScrollableColumns";
import Pagination from "../Pagination";
import { Column, IPagingState, ISortState } from "./utils/types";
import "./Table.css";
import { onRowFunction } from "./utils/utils";
import HeaderMini from "./Header/HeaderMini";
import NoDataCell from "./NoDataCell";
import { createUniqueKey } from "../../utils";

// -----------------
// Type Definitions
// -----------------

export type HeaderControls = {
  selectControlsLabel?: {
    selectAll?: string;
    reset?: string;
  };
  sortByLabel?: string;
};

export type TableMiniProps = {
  table?: RefObject<HTMLDivElement>;
  columns?: Column[];
  data?: RowData[];
  footer?: Record<string, string | number | ((...args: any[]) => any)> | null;
  mini?: boolean;
  paging?: IPagingState | undefined;
  sorting?: ISortState | undefined;
  scrollable?: boolean;
  tableBodyStyle?: React.CSSProperties;
  expandedRows?: string[];
  pagingTotal: number;
  noDataText?: string;
  headerControls: HeaderControls;
  classNameTable?: string;
  classNameTableWrapper?: string;
  onRow?: ((row: RowData) => void) | null;
  onMouseDown?: (e: React.MouseEvent) => void;
  changeSorting?: (sorting: any) => void;
  expandOnClick?: (key: string | number) => void;
  changePagination?: (page: any) => void;
  noHeader?: boolean;
};

// -----------------
// Component
// -----------------
function TableMini({
  table,
  columns = [],
  data = [],
  footer = null,
  mini = true,
  paging = undefined,
  sorting = {},
  scrollable = false,
  tableBodyStyle = {},
  expandedRows = [],
  pagingTotal,
  noDataText = "No data",
  headerControls,
  classNameTable = "",
  classNameTableWrapper = "",
  onRow = null,
  onMouseDown = () => {},
  changeSorting = () => {},
  expandOnClick = () => {},
  changePagination = () => {},
  noHeader = false,
}: TableMiniProps) {
  return (
    <div
      ref={table}
      role="presentation"
      className={classNameTableWrapper}
      onMouseDown={onMouseDown}
      onPointerDown={onMouseDown}
    >
      {!noHeader && (
        <HeaderMini
          columns={columns}
          sorting={sorting}
          changeSorting={changeSorting}
          headerControls={headerControls}
        />
      )}
      <div className="Table-content" style={tableBodyStyle}>
        <table className={classNameTable}>
          <ScrollableColumns scrollable={scrollable} columns={columns} mini={mini} />
          <tbody>
            {data?.length ? (
              data?.map((item, index) => (
                <Row
                  data={item}
                  index={index}
                  columns={columns}
                  scrollable={scrollable}
                  expandedRows={expandedRows}
                  onRow={onRowFunction(onRow)}
                  expandOnClick={expandOnClick}
                  key={createUniqueKey(index, item.key)}
                />
              ))
            ) : (
              <NoDataCell colSpan={columns.length} noDataText={noDataText} />
            )}
          </tbody>
          {!scrollable && footer && <Footer columns={columns} footer={footer} />}
        </table>
      </div>
      {pagingTotal > data.length && (
        <Pagination {...paging} total={pagingTotal} onChange={changePagination} />
      )}
    </div>
  );
}

export default TableMini;
